import {
  SCHEDULE_TIMES,
  firstOpenSlot as sharedFirstOpenSlot,
  formatScheduleTime,
  isoDateIn,
  isScheduleTime,
  scheduleDate,
  slotHasPassed,
  timeToHM,
  type ScheduleTime,
} from '@str/shared';
import { DAYS, shortDate } from './format';
import { whenParts } from './home';

export type { ScheduleTime };

/** True for one of the three quick picks; a custom minute is anything else. */
export function isPreset(time: ScheduleTime): boolean {
  return (SCHEDULE_TIMES as readonly string[]).includes(time);
}

/** "9:07 PM" → "21:07", the value a browser time input holds. */
export function toInputTime(time: ScheduleTime): string {
  const [h, m] = timeToHM(time);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** "21:07" → "9:07 PM", the form the API expects; null while the input is empty or partial. */
export function fromInputTime(value: string): ScheduleTime | null {
  const m = /^(\d{2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const time = formatScheduleTime(Number(m[1]), Number(m[2]));
  return isScheduleTime(time) ? time : null;
}

/** The host's IANA zone as the browser reports it: the clock their schedule times are read on. */
export function hostTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** The zone a schedule request travels with: the one the post was scheduled in, else the browser's. */
export function scheduleZone(post: { timezone?: string | null } | null | undefined): string {
  return post?.timezone || hostTimeZone();
}

/** The instant a day + preset happens in a zone: the same number the server computes. */
export function slotInstant(date: string, time: ScheduleTime, zone: string): Date {
  return scheduleDate(date, time, zone);
}

/** True once that slot is not ahead of `now` any more; the server refuses it then. */
export function isSlotPast(date: string, time: ScheduleTime, zone: string, now = new Date()) {
  return slotHasPassed(date, time, zone, now);
}

/** A pick moved forward past anything that has gone by: later today, else the next day's first. */
export function firstOpenSlot(date: string, time: ScheduleTime, zone: string, now = new Date()) {
  return sharedFirstOpenSlot(date, time, zone, now);
}

/** The last minute a day can take a post: "today" lasts until this goes by on the property clock. */
const LAST_MINUTE: ScheduleTime = '11:59 PM';

/** True once a whole day has gone by in a zone; today is not over while any minute is ahead. */
export function isDayOver(date: string, zone: string, now = new Date()): boolean {
  return slotHasPassed(date, LAST_MINUTE, zone, now);
}

/**
 * The pick moved forward on its own day: as given while it is ahead, else the next preset left,
 * else the next quarter hour still ahead today (the last minute at worst). Only a day that is
 * over runs on to the next day's first preset.
 */
export function openSlotOn(
  date: string,
  time: ScheduleTime,
  zone: string,
  now = new Date(),
): { date: string; time: ScheduleTime } {
  if (!slotHasPassed(date, time, zone, now)) return { date, time };
  const preset = SCHEDULE_TIMES.find((t) => !slotHasPassed(date, t, zone, now));
  if (preset) return { date, time: preset };
  if (isDayOver(date, zone, now)) return sharedFirstOpenSlot(date, time, zone, now);
  const [h, m] = wallTime(now, zone);
  // A quarter hour with at least fifteen minutes to spare, so it is not gone before it is saved.
  const next = Math.ceil((h * 60 + m + 15) / 15) * 15;
  if (next >= 24 * 60) return { date, time: LAST_MINUTE };
  const t = formatScheduleTime(Math.floor(next / 60), next % 60);
  return { date, time: slotHasPassed(date, t, zone, now) ? LAST_MINUTE : t };
}

/** [hour, minute] an instant shows on a zone's wall clock. */
function wallTime(at: Date, zone: string): [number, number] {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(at);
  const n = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return [n('hour') % 24, n('minute')];
}

/** "Thu 6:30 PM" → weekday index (Mon = 0) and preset time. */
export function parseBestTime(best: string): { day: number; time: ScheduleTime } {
  const [d = 'Thu', ...rest] = best.split(' ');
  const day = Math.max(0, (DAYS as readonly string[]).indexOf(d));
  const t = rest.join(' ');
  const time = (SCHEDULE_TIMES as readonly string[]).includes(t) ? t : '6:30 PM';
  return { day, time };
}

/**
 * Where the suggested time came from: `audience` once it is learned from the account's own
 * published results, `default` while it is only a good time for short-term-rental posts (the
 * property's plan picks it from the listing, not from anyone's audience).
 */
export type BestTimeSource = 'audience' | 'default';

/** The Share step's suggestion line: confident only when it really comes from the account. */
export function bestTimeCopy(
  when: string,
  source: BestTimeSource,
): { lead: string; when: string; tail: string } {
  if (source === 'audience') return { lead: 'Your audience is most active', when, tail: '' };
  return {
    lead: 'A good time to post:',
    when,
    tail: "We'll learn your audience's best times once posts go out.",
  };
}

/** "EDT": a zone's short name at an instant, so summer and winter read right. */
export function zoneAbbr(at: Date, zone: string): string {
  const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
    .formatToParts(at)
    .find((p) => p.type === 'timeZoneName');
  return part?.value ?? zone;
}

/** "Oct 9, 4:46 PM EDT": a scheduled post's day and time, naming the clock it was scheduled in. */
export function scheduledLabel(post: {
  scheduledAt: string | null;
  scheduledDate: string | null;
  scheduledTime: string | null;
  timezone: string | null;
}): string | null {
  if (!post.scheduledAt) return null;
  const { time } = whenParts(post.scheduledAt, post.scheduledTime, post.timezone);
  const day = shortDate(post.scheduledDate ?? isoDateIn(new Date(post.scheduledAt), post.timezone));
  const zone = zoneAbbr(new Date(post.scheduledAt), post.timezone ?? 'UTC');
  return `${day}, ${time} ${zone}`;
}
